import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  allRoutines, draftSessions, finishedSessions, exerciseMap, outboxCounts,
  getLastGymId, setLastGymId,
} from '../../db/repo';
import { createDraftFromRoutine, createEmptyDraft } from '../session/createSession';
import { todayISO, weekdayOf, formatDate, daysAgoLabel, timeAgoLabel } from '../../lib/dates';
import { isSyncConfigured, getLastSyncAt } from '../../sync/config';
import { buildWeek } from './weekPlan';
import { GYMS, DEFAULT_GYM_ID, gymLabel } from '../../domain/gym';
import type { GymId, Routine } from '../../domain/types';

export function TodayScreen() {
  const navigate = useNavigate();
  const routines = useLiveQuery(() => allRoutines(), []);
  const drafts = useLiveQuery(() => draftSessions(), []);
  const recent = useLiveQuery(() => finishedSessions(), []);
  const outbox = useLiveQuery(() => outboxCounts(), []);
  const lastSync = useLiveQuery(() => getLastSyncAt(), []);

  // Finished sessions saved locally but not yet mirrored to Notion.
  const unsynced = (outbox?.pending ?? 0) + (outbox?.error ?? 0);
  const configured = isSyncConfigured();

  const today = todayISO();
  const todayWeekday = weekdayOf(today);
  const week = buildWeek(routines ?? []);

  // Which gym today's session is in. Pre-selected from last time and confirmed by
  // a single tap before starting, because it decides which history the targets come
  // from — getting it wrong silently mis-targets every load in the session.
  // Undefined until the stored value loads; starting a session before then could
  // stamp the default over a remembered Fitness First, so Start waits for it.
  const storedGymId = useLiveQuery(() => getLastGymId(), []);
  const gymId = storedGymId ?? DEFAULT_GYM_ID;
  const gymReady = storedGymId != null;

  async function pickGym(next: GymId) {
    await setLastGymId(next);
  }

  async function start(routine: Routine) {
    const exById = await exerciseMap();
    const session = await createDraftFromRoutine(routine, exById, gymId);
    navigate(`/session/${session.id}`);
  }

  async function startEmpty() {
    const session = await createEmptyDraft(gymId);
    navigate(`/session/${session.id}`);
  }

  return (
    <div className="screen">
      <div className="spread">
        <div>
          <div className="screen-title" style={{ marginBottom: 2 }}>This week</div>
          <div className="faint" style={{ fontSize: 13 }}>{formatDate(today)}</div>
        </div>
        {!configured ? (
          <span className="chip">Offline only</span>
        ) : (
          <span className={`chip ${unsynced > 0 ? 'warn' : 'good'}`}>
            {lastSync ? `Synced ${timeAgoLabel(lastSync)}` : 'Not synced yet'}
          </span>
        )}
      </div>

      {unsynced > 0 && !configured && (
        <button
          className="list-tap"
          style={{ borderColor: 'var(--warn, #b06a00)', marginTop: 10 }}
          onClick={() => navigate('/more/settings')}
        >
          <div>
            <strong>{unsynced} session{unsynced > 1 ? 's' : ''} saved here, not synced</strong>
            <div className="faint" style={{ fontSize: 13 }}>
              Sync isn't set up. Add your Worker URL & secret to push them to Notion.
            </div>
          </div>
          <span className="chip">Set up</span>
        </button>
      )}

      {unsynced > 0 && configured && (outbox?.error ?? 0) > 0 && (
        <button
          className="list-tap"
          style={{ borderColor: 'var(--warn, #b06a00)', marginTop: 10 }}
          onClick={() => navigate('/more/settings')}
        >
          <div>
            <strong>{unsynced} session{unsynced > 1 ? 's' : ''} waiting to sync</strong>
            <div className="faint" style={{ fontSize: 13 }}>
              Last sync had errors. Open Settings and tap Save &amp; sync now.
            </div>
          </div>
          <span className="chip">Retry</span>
        </button>
      )}

      {drafts && drafts.length > 0 && (
        <>
          <div className="section-label">In progress</div>
          {drafts.map((d) => (
            <button key={d.id} className="list-tap" onClick={() => navigate(`/session/${d.id}`)}>
              <div>
                <strong>{d.routineLabel}</strong>
                <div className="faint" style={{ fontSize: 13 }}>
                  Started {formatDate(d.date)} - {d.entries.length} exercises
                </div>
              </div>
              <span className="chip accent">Resume</span>
            </button>
          ))}
        </>
      )}

      <div className="section-label">Gym</div>
      <div className="card tight">
        <div className="segmented full" aria-label="Gym">
          {GYMS.map((g) => (
            <button
              key={g.id}
              type="button"
              className={g.id === gymId ? 'on' : ''}
              onClick={() => pickGym(g.id)}
            >
              {g.shortName}
            </button>
          ))}
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 8 }}>
          Targets and the +/- steps come from your history at this gym.
        </div>
      </div>

      <div className="section-label">Your week</div>
      {week.map((d) => {
        const isToday = d.weekday === todayWeekday;
        const dayChip = isToday ? <span className="chip accent">Today</span> : null;

        if (!d.routine) {
          return (
            <div key={d.weekday} className="card tight" style={{ opacity: isToday ? 1 : 0.7 }}>
              <div className="card-row">
                <div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="day-name">{d.name}</span>
                    {dayChip}
                  </div>
                  <div className="faint" style={{ fontSize: 13, marginTop: 2 }}>
                    {d.restLabel}{d.restSub ? ` - ${d.restSub}` : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        const routine = d.routine;
        return (
          <div
            key={d.weekday}
            className="card tight"
            style={isToday ? { borderColor: 'var(--accent)' } : undefined}
          >
            <div className="card-row">
              <div className="grow">
                <div className="row" style={{ gap: 8 }}>
                  <span className="day-name">{d.name}</span>
                  {dayChip}
                </div>
                <strong style={{ display: 'block', marginTop: 2 }}>{routine.dayLabel}</strong>
                <div className="faint" style={{ fontSize: 12 }}>{routine.slots.length} exercises</div>
              </div>
              <button className={`btn ${isToday ? 'primary' : 'sm'}`} disabled={!gymReady} onClick={() => start(routine)}>Start</button>
            </div>
          </div>
        );
      })}

      <button className="btn ghost full" style={{ marginTop: 4 }} disabled={!gymReady} onClick={startEmpty}>
        Start a freestyle session
      </button>

      <div className="section-label">Recent sessions</div>
      {recent && recent.length === 0 && <div className="empty">No finished sessions yet.</div>}
      {recent?.slice(0, 4).map((s) => (
        <button key={s.id} className="list-tap" onClick={() => navigate(`/session/${s.id}`)}>
          <div>
            <strong>{s.routineLabel}</strong>
            <div className="faint" style={{ fontSize: 13 }}>
              {formatDate(s.date)} - {daysAgoLabel(s.date)} - {gymLabel(s.gymId)}
            </div>
          </div>
          <div className="row">
            {s.coaching && <span className="chip good">Coached</span>}
            <span className="faint">{s.entries.length} ex</span>
          </div>
        </button>
      ))}
    </div>
  );
}
